import { useState } from 'react'
import Cropper from 'react-easy-crop'
import styled from 'styled-components'

import { Icon, Slider, Text } from '../index'
import { CropShape, Size, MediaSize, PixelCrop } from './types'

type PhotoEditorProps = {
  imageUrl: string
  square?: boolean
  containerSize?: Size
  cropShape?: CropShape
  cropSize?: Size
  onCropComplete?: (croppedArea: PixelCrop, croppedAreaPixels: PixelCrop, croppedAreaSize: Size) => void
}

const RANGE = {
  min: 1,
  max: 3,
  step: 0.1,
}

const INCREMENT = 0.1

const PhotoEditor = ({
  containerSize = {
    width: 360,
    height: 360,
  },
  cropShape = 'rect',
  cropSize,
  imageUrl,
  onCropComplete,
}: PhotoEditorProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)

  const onMediaLoaded = (mediaSize: MediaSize) => {
    const mediaHeight = Number(containerSize?.height) / mediaSize.height
    const mediaWidth = Number(containerSize?.width) / mediaSize.width
    setZoom(Math.max(mediaHeight, mediaWidth))
  }

  const onZoomChange = (value: number) => {
    setZoom(value)
  }

  const handleCropComplete = (croppedArea: PixelCrop, croppedAreaPixels: PixelCrop) => {
    const croppedAreaSize: Size = {
      width: Number(cropSize?.width ?? containerSize?.width),
      height: Number(cropSize?.height ?? containerSize?.height),
    }
    onCropComplete?.(croppedArea, croppedAreaPixels, croppedAreaSize)
  }

  return (
    <Container>
      <Cropper
        image={imageUrl}
        crop={crop}
        cropShape={cropShape}
        cropSize={cropSize ?? containerSize}
        restrictPosition={false}
        showGrid={false}
        zoom={zoom}
        onCropChange={setCrop}
        onCropComplete={handleCropComplete}
        onZoomChange={setZoom}
        onMediaLoaded={onMediaLoaded}
        style={{
          containerStyle: {
            ...containerSize,
          },
        }}
      />
      <ControlContainer>
        <Icon color="gray5" icon="arrows-alt" />
        <Text inline color="gray5" ml="10px">
          Drag to reposition
        </Text>
        <Slider increment={INCREMENT} range={RANGE} value={zoom} onChange={onZoomChange} />
      </ControlContainer>
    </Container>
  )
}

const Container = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: center;
  height: 480px;
  position: relative;
  width: 360px;
`

const ControlContainer = styled.div`
  width: 360px;
  height: 100px;
  text-align: center;
  user-select: none;
`

export default PhotoEditor
